import type React from "react";
import { lazy, Suspense, useCallback, useEffect, useMemo } from "react";

import { clamp } from "framer-motion";
import {
  BottomSheet,
  type BottomSheetRef as LibBottomSheetRef,
} from "react-spring-bottom-sheet";
import type {
  defaultSnapProps,
  snapPoints,
} from "react-spring-bottom-sheet/dist/types";

import { selectIsCalibrating } from "@/features/calibration/calibrationSlice";
import { unsetCurrentPoiID } from "@/features/cartography/cartographySlice";
import {
  AppUI,
  selectUIMode,
  selectUserSettingsPanelOpen,
  setUIMode,
} from "../app/appSlice";
import { selectConfigItem } from "../config/configSlice";
import {
  selectCurrentLocationBuildingId,
  selectHasCurrentLocation,
  selectHasOOBLocation,
  selectLocationStatus,
} from "../location/locationSlice";
import {
  setNavigationFrom,
  setNavigationTo,
} from "../navigation/navigationSlice";

import {
  useAppDispatch,
  useAppSelector,
  useWindowDimensions,
} from "@/app/hooks";
import { ENABLE_PATH_FINDING } from "@/constants";
import {
  selectCurrentBuilding,
  selectCurrentPoiID,
} from "@/features/cartography/cartographySlice.selectors";
import { CURRENT_USER_LOCATION_ID } from "@/libs/models";
import { handleRemoveCurrentPoiCategories } from "../cartography/actions";
// const LanguageSelector = lazy(
//   () => import("../map/components/left_toolbar/language/LanguageSelector"),
// );
import { LocationStatusTypes } from "../utils/types";
import { BottomDrawerState, useBottomDrawer } from "./BottomDrawerContext";
import DirectionsShortcutButton from "./DirectionsShortcutButton";
import { useBottomDrawerHelpers } from "./hooks/useBottomDrawerHelpers";

const ZoomInOutButtons = lazy(
  () => import("../map/components/map_toolbar_elements/ZoomInOutButtons"),
);
const FindMyCarButton = lazy(() => import("../find_my_car/FindMyCarButton"));
const CalibrationControls = lazy(
  () => import("../calibration/components/CalibrationControls"),
);
const ARButton = lazy(() => import("../ar/ARButton"));
const MobileFloorSelector = lazy(
  () => import("../location_explorer/MobileFloorSelector"),
);
const LocationStatusButton = lazy(
  () => import("../location/LocationStatusButton"),
);
const FeedbackButton = lazy(() => import("../feedback/FeedbackButton"));
const TicketSelector = lazy(() => import("../app/tickets/TicketSelector"));
const UserSettingsButton = lazy(
  () => import("../config/components/user_settings_panel/UserSettingsButton"),
);
const ShareLocationButton = lazy(
  () => import("../share_location/ShareLocationButton"),
);

import "./bottom-drawer.scss";

export interface BottomDrawerRef extends LibBottomSheetRef {
  snapToMin: () => void;
  snapToMax: () => void;
  onScroll?: () => void;
}

// Easing function for siblings
const easing = (progress: number) => {
  if (progress <= 0.3) {
    return 0;
  } else {
    return clamp((progress - 0.3) / 0.1, 0, 1);
  }
};

const BottomDrawer: React.FC = () => {
  // Redux
  const dispatch = useAppDispatch();
  const uiMode = useAppSelector(selectUIMode);
  const showDirectionsShortcut = useAppSelector(
    selectConfigItem("ui.widget_visibility.directions_shortcut"),
  );
  const hasCurrentLocation = useAppSelector(selectHasCurrentLocation);
  const hasOOBLocation = useAppSelector(selectHasOOBLocation);
  const currentLocationBuildingId = useAppSelector(
    selectCurrentLocationBuildingId,
  );
  const currentBuilding = useAppSelector(selectCurrentBuilding);
  const currentPoiID = useAppSelector(selectCurrentPoiID);
  const findMyCarActive = useAppSelector(
    selectConfigItem("feature_flags.find_my_car_active"),
  );
  const arIntegration = useAppSelector(
    selectConfigItem("feature_flags.ar_integration"),
  );
  const locationStatus = useAppSelector(selectLocationStatus);
  const ticketSelector = useAppSelector(
    selectConfigItem("feature_flags.ticket_selector"),
  );
  // const showLanguageSelector = useAppSelector(
  //   selectConfigItem("ui.widget_visibility.language_control"),
  // );
  const zoomButtonsVisible = useAppSelector(
    selectConfigItem("ui.widget_visibility.zoom_buttons_mobile"),
  );
  const appMode = useAppSelector(selectUIMode);
  const isCalibrating = useAppSelector(selectIsCalibrating);
  const showFloorSelector = useAppSelector(
    selectConfigItem("ui.widget_visibility.floor_selector"),
  );
  const browserPositioning = useAppSelector(
    selectConfigItem("feature_flags.browser_positioning"),
  );
  const feedbackFormEnabled = useAppSelector(
    selectConfigItem("feature_flags.feedback_form"),
  );

  const userSettingsPanelOpen = useAppSelector(selectUserSettingsPanelOpen);

  // Hooks
  const {
    state,
    content,
    header,
    footer,
    snapPoints,
    defaultSnapPoint,
    bottomDrawerRef,
    setBottomDrawerRef,
    dragProgress,
    setBottomDrawerHeight,
    isBottomDrawerReady,
    setIsBottomDrawerReady,
    expandOnDrag,
    hideSiblings,
  } = useBottomDrawer();
  const { snapToMinBottomDrawer, snapToMaxBottomDrawer } =
    useBottomDrawerHelpers();
  const { height } = useWindowDimensions();

  const getDefaultSnapPoints: snapPoints = ({
    maxHeight,
    minHeight,
    headerHeight,
    footerHeight,
  }) => [Math.min(maxHeight * 0.9, minHeight), footerHeight + headerHeight];

  const getDefaultSnap = ({ minHeight }: defaultSnapProps) => minHeight;

  const onBackPressed = (event: PopStateEvent) => {
    if (bottomDrawerRef) {
      snapToMinBottomDrawer();
      event.preventDefault();
      event.stopPropagation();
    }
  };

  // Memos
  const showLocationStatusButton = useMemo(() => {
    return (
      browserPositioning ||
      (locationStatus && locationStatus !== LocationStatusTypes.OUTDOOR)
    );
  }, [locationStatus, browserPositioning]);

  // Back button event for mobile devices
  useEffect(() => {
    window.addEventListener("popstate", onBackPressed);
    return () => {
      window.removeEventListener("popstate", onBackPressed);
    };
  }, []);

  const onRefChange = useCallback((node: BottomDrawerRef) => {
    if (!node) return;
    node.snapToMin = snapToMinBottomDrawer;
    node.snapToMax = snapToMaxBottomDrawer;
    setBottomDrawerRef(node);
  }, []);

  useEffect(() => {
    setBottomDrawerHeight(bottomDrawerRef?.height || 0);
  }, [bottomDrawerRef?.height]);

  // Wait until height is non-null
  if (height === 0) return <></>;

  return (
    <BottomSheet
      ref={onRefChange}
      open={state !== BottomDrawerState.closed}
      blocking={false}
      expandOnContentDrag={expandOnDrag}
      // Avoid locking scroll event on iOS
      scrollLocking={false}
      defaultSnap={defaultSnapPoint || getDefaultSnap}
      snapPoints={
        state === BottomDrawerState.minimized
          ? () => 0
          : snapPoints != null
            ? snapPoints
            : getDefaultSnapPoints
      }
      header={state != BottomDrawerState.minimized && header}
      footer={state != BottomDrawerState.minimized && footer}
      aria-hidden={
        state == BottomDrawerState.minimized ||
        state == BottomDrawerState.closed ||
        userSettingsPanelOpen
      }
      sibling={
        <>
          {(uiMode != AppUI.calibration || !isCalibrating) &&
            showFloorSelector && (
              <div
                style={{
                  opacity: 1 - easing(dragProgress),
                }}
                className={`bottom-drawer-sibling bottom-drawer-sibling--left ${
                  hideSiblings ? "bottom-drawer-sibling--hidden" : ""
                }`}
                aria-hidden={true}
              >
                <Suspense>
                  <MobileFloorSelector />
                </Suspense>
              </div>
            )}
          <div
            style={{
              opacity: 1 - easing(dragProgress),
            }}
            className={`bottom-drawer-sibling bottom-drawer-sibling--right ${
              hideSiblings ? "bottom-drawer-sibling--hidden" : ""
            }`}
            aria-hidden={userSettingsPanelOpen}
          >
            {ENABLE_PATH_FINDING && showDirectionsShortcut && !currentPoiID && (
              <DirectionsShortcutButton
                shown={uiMode === AppUI.explore}
                onClick={() => {
                  const poiID = currentPoiID;

                  dispatch(unsetCurrentPoiID());
                  handleRemoveCurrentPoiCategories();
                  // Set automatically CURRENT_USER_LOCATION_ID in case
                  // we are requesting directions from inside the same building we are positioning.
                  if (
                    hasCurrentLocation &&
                    currentBuilding?.id == currentLocationBuildingId &&
                    !hasOOBLocation
                  ) {
                    dispatch(setNavigationTo(poiID));
                    dispatch(setNavigationFrom(CURRENT_USER_LOCATION_ID));
                  } else {
                    dispatch(setNavigationTo(poiID));
                  }
                  dispatch(setUIMode(AppUI.directions));
                }}
              />
            )}
            <Suspense>
              <ShareLocationButton />
            </Suspense>
            <Suspense>
              <UserSettingsButton />
            </Suspense>
            <Suspense>
              <ZoomInOutButtons visible={zoomButtonsVisible} />
            </Suspense>
            <Suspense>
              {ticketSelector && appMode == AppUI.directions && (
                <TicketSelector />
              )}
            </Suspense>
            {findMyCarActive && uiMode == AppUI.explore && (
              <Suspense>
                <FindMyCarButton />
              </Suspense>
            )}
            {uiMode == AppUI.calibration && (
              <Suspense>
                <CalibrationControls />
              </Suspense>
            )}
            {arIntegration && uiMode == AppUI.navigation && (
              <Suspense>
                <ARButton />
              </Suspense>
            )}
            <Suspense>
              {uiMode === AppUI.explore && feedbackFormEnabled && (
                <FeedbackButton />
              )}
            </Suspense>
            <Suspense>
              {showLocationStatusButton && <LocationStatusButton />}
            </Suspense>

            {/* {showLanguageSelector && (
              <Suspense>
                {" "}
                <LanguageSelector />
              </Suspense>
            )} */}
          </div>
        </>
      }
      maxHeight={height}
      onSpringStart={() => {
        requestAnimationFrame(() => {
          // Obtain height of drawer after animation
          setBottomDrawerHeight(bottomDrawerRef?.height || 0);
        });
      }}
      onWheel={() => {
        bottomDrawerRef?.onScroll && bottomDrawerRef.onScroll();
      }}
      onTouchMove={() => {
        bottomDrawerRef?.onScroll && bottomDrawerRef.onScroll();
      }}
      onSpringEnd={(event) => {
        setBottomDrawerHeight(bottomDrawerRef?.height || 0);

        // Wait until BottomSheet is ready to perform animations
        if (event.type == "OPEN" && !isBottomDrawerReady) {
          setIsBottomDrawerReady(true);
        }
      }}
    >
      {state != BottomDrawerState.minimized && content}
    </BottomSheet>
  );
};
export default BottomDrawer;
